import * as _ from 'lodash';
import { QuillDeltaToHtmlConverter } from 'quill-delta-to-html';
import { useMemo } from 'react';

import { createLocalStorageInstance } from '../../helpers/localStorage';
import useActiveBannersHook, { getActiveBannersQueryKey } from '../../hooks/api/banners/useActiveBanners';
import queryClient from '../../ReactQuery/client';
import { parseContentsJSON } from '../generic/QuillEditor';
import type { DisplayBanner } from '../../../shared/types/routes/banners';
import type { ActiveBannerPlacement } from '../../hooks/api/banners/useActiveBanners';

type BannerIds = number[];

type RenderedDisplayBanner = DisplayBanner & { renderedBody: string };

const BANNERS_LS_KEY = '__FOSSA_BANNERS_DISMISSED';
const ls = createLocalStorageInstance<BannerIds>(BANNERS_LS_KEY);

/**
 * Determine whether the given banner should be displayed (whether it hasn't been dismissed).
 *
 * @param {BannerIds | null} dismissedBanners - List of dismissed banners.
 * @param {DisplayBanner} banner - Given banner to check for.
 */
function shouldDisplayBanner(
  dismissedBanners: BannerIds | null,
  { id: bannerId }: DisplayBanner,
): boolean {
  if (Array.isArray(dismissedBanners)) {
    return !dismissedBanners.includes(bannerId);
  }
  return true;
}

type UseActiveBanners = {
  isLoading: boolean;
  error: Error | null;
  banners: RenderedDisplayBanner[];
  onDismissBanner: (bannerId: number) => void;
};

/**
 * Manages fetching all active banners for the given placement, and managing dismissed banners.
 */
const useActiveBanners = (placement?: ActiveBannerPlacement): UseActiveBanners => {
  const { data: banners, isLoading, error } = useActiveBannersHook(placement);
  const dismissedBanners = ls.get();

  //  Generates the banners that should be displayed.
  const renderedBanners: RenderedDisplayBanner[] = useMemo(() => {
    //  Filter the banners that should be displayed.
    const filteredBanners = banners?.filter(banner => shouldDisplayBanner(dismissedBanners, banner)) ?? [];
    //  Sort the banners by ID and take the first three.
    const sortedBanners = _.sortBy(filteredBanners, (banner) => banner.id).slice(0, 3);
    return sortedBanners.map(banner => ({
      ...banner,
      renderedBody: (new QuillDeltaToHtmlConverter(parseContentsJSON(banner.body), {})).convert(),
    }));
  }, [banners, dismissedBanners]);

  /**
   * Dismisses a banner by:
   * 1. Removing it from the active banners query cache for this placement
   * 2. Adding its ID to the list of dismissed banners in localStorage so it stays
   *    hidden across all placements (dismissal is keyed by banner ID, not placement)
   *
   * @param {number} bannerId - ID of the banner to dismiss
   */
  function onDismissBanner(bannerId: number): void {
    // Filter out the dismissed banner from the cached active banners
    const nextBanners = banners?.filter(banner => banner.id !== bannerId) ?? [];
    queryClient.setQueryData(getActiveBannersQueryKey(placement), nextBanners);

    // Add the banner ID to the dismissed banners list in localStorage
    ls.set(
      Array.isArray(dismissedBanners)
        ? [...dismissedBanners, bannerId] // Append to existing list
        : [bannerId], // Create new list if none exists
    );
  }

  return {
    isLoading,
    error,
    banners: renderedBanners,
    onDismissBanner,
  };
};

export default useActiveBanners;
